import {
  Box,
  Chip,
  Grid,
  TextField,
  Typography
  } from "@mui/material";
import { useMemo, useRef, useState } from "react";

interface FilterableListProps<T> {
  items: T[];
  onToggleItem: (item: T) => void;
  filterPlaceholder: string;
  getLabel: (item: T) => string;
  title: string;
  /**
   * Gdy włączone: naciśnięcie Enter przy jednym wyniku przerzuca element,
   * czyści pole i utrzymuje fokus w polu wyszukiwania.
   */
  enableEnterAutoSelect?: boolean;
}

export function FilterableList<T>({
  items,
  onToggleItem,
  filterPlaceholder,
  getLabel,
  title,
  enableEnterAutoSelect = false,
}: FilterableListProps<T>) {
  const [filter, setFilter] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filteredItems = useMemo(() => {
    return items.filter((item) => getLabel(item).toLowerCase().includes(filter.toLowerCase()));
  }, [items, filter, getLabel]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!enableEnterAutoSelect) return;
    if (e.key !== "Enter") return;
    if (filteredItems.length !== 1) return;
    e.preventDefault();
    const [onlyItem] = filteredItems;
    if (!onlyItem) return;
    onToggleItem(onlyItem);
    setFilter("");
    // Utrzymaj fokus w polu po wyczyszczeniu wartości
    // (timeout pozwala Reactowi zaktualizować value zanim przywrócimy fokus)
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
          {title} ({items.length})
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={`${title} (${items.length})`}
          placeholder={filterPlaceholder}
          variant="outlined"
          fullWidth
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
        />
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          {filteredItems.slice(0, 199).map((item, index) => (
            <Chip
              key={getLabel(item) + "_" + index}
              label={getLabel(item)}
              onClick={() => onToggleItem(item)}
              clickable
              sx={{
                backgroundColor: "#e8eaed",
                "&:hover": {
                  backgroundColor: "#d1d5db",
                },
              }}
            />
          ))}
          {filteredItems.length > 199 && (
            <Chip
              label={`Więcej - wyszukaj... (${filteredItems.length - 199})`}
              color="info"
              sx={{
                cursor: "default",
                backgroundColor: "info.light",
                "&:hover": {
                  backgroundColor: "info.primary",
                },
              }}
            />
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
